import type { AnySchema, Asserts } from 'yup';
import * as yup from 'yup';

const requiredNumber = () => yup.number().required();

const headcountInactiveSchema = yup
  .object({
    maternity: requiredNumber(),
    military: requiredNumber(),
    sabbatical: requiredNumber(),
  })
  .strict(true)
  .required();

const headcountGenderSchema = yup
  .object({
    male: requiredNumber(),
    female: requiredNumber(),
  })
  .strict(true)
  .required();

const headcountLocationSchema = yup
  .object({
    lviv: requiredNumber(),
    ukraineRemote: requiredNumber(),
    abroad: requiredNumber(),
  })
  .strict(true)
  .required();

const headcountTenureSchema = yup
  .object({
    lt2: requiredNumber(),
    y2to5: requiredNumber(),
    y5to10: requiredNumber(),
    gt10: requiredNumber(),
  })
  .strict(true)
  .required();

const headcountMomRowSchema = yup
  .object({
    month: requiredNumber(),
    active: requiredNumber(),
    // TODO(HRM-39): tighten back to required() once BE >= SCHEMA_VERSION 3 is everywhere
    bench: yup.number().notRequired(),
    inactive: requiredNumber(),
  })
  .strict(true);

const headcountYearlyTrendRowSchema = yup
  .object({
    year: requiredNumber(),
    avgMonthlyHeadcount: requiredNumber(),
  })
  .strict(true);

const headcountProjectRowSchema = yup
  .object({
    project: yup.string().required(),
    headcount: requiredNumber(),
  })
  .strict(true);

const headcountProjectGroupRowSchema = yup
  .object({
    // defined() (not required): empty string must not fail the whole dashboard payload
    group: yup.string().defined(),
    projectCount: requiredNumber(),
    headcount: requiredNumber(),
  })
  .strict(true);

export const headcountDashboardApiBodySchema = yup
  .object({
    year: requiredNumber(),
    activeEmployees: requiredNumber(),
    inactive: headcountInactiveSchema,
    averageTenureYears: requiredNumber(),
    newJoinersInYear: requiredNumber(),
    // TODO(HRM-39): tighten back to required() once BE >= SCHEMA_VERSION 3 is everywhere
    benchEmployees: yup.number().notRequired(),
    priorActiveEmployees: yup.number().notRequired(),
    priorInactive: headcountInactiveSchema.notRequired(),
    priorAverageTenureYears: yup.number().notRequired(),
    priorNewJoinersInYear: yup.number().notRequired(),
    priorMonthBenchEmployees: yup.number().notRequired(),
    avgMonthlyHeadcountSelectedYear: requiredNumber(),
    avgMonthlyHeadcountPriorYear: requiredNumber(),
    gender: headcountGenderSchema,
    location: headcountLocationSchema,
    tenure: headcountTenureSchema,
    mom: yup.array(headcountMomRowSchema).length(12).required(),
    // TODO(HRM-39): tighten back to required() once BE >= SCHEMA_VERSION 3 is everywhere
    projects: yup.array(headcountProjectRowSchema).notRequired(),
    projectGroups: yup.array(headcountProjectGroupRowSchema).notRequired(),
    yearlyTrend: yup.array(headcountYearlyTrendRowSchema).nullable().notRequired(),
  })
  .strict(true);

export const headcountYearlyTrendEnvelopeSchema = yup
  .object({
    data: yup.array(headcountYearlyTrendRowSchema).required(),
  })
  .strict(true);

export function assertHeadcountApiValid<S extends AnySchema>(
  schema: S,
  value: unknown,
): Asserts<S> {
  try {
    return schema.validateSync(value, { abortEarly: false, strict: true });
  } catch (e: unknown) {
    if (e instanceof yup.ValidationError) {
      throw new Error(`Headcount API: ${e.errors.join('; ')}`);
    }
    throw e;
  }
}
